import { useRef, useCallback } from 'preact/hooks';
import { useReorderableItem } from '../hooks/PRIVATE_useReorderable';
import { useUser } from '../hooks/UNSAFE_useUser';
import { classNames } from '../utils/UNSAFE_classNames';
import { ITEM_SELECTOR } from '../utils/PRIVATE_collectionUtils';
import { ListItemRendererContext } from '../UNSAFE_Collection';
import { ReorderableItemProps } from '../UNSAFE_Collection/Reorderable.types';
import { multiVariantStyles, styles } from './themes/BaseCardViewStyles.css';
import { GutterSize } from './BaseCardViewUtils';

export type BaseCardViewReorderableItemProps<K> = ReorderableItemProps<K> & {
  currentKey: K | undefined;
  gutterSize: GutterSize;
  columns: number;
};

/**
 * The internal component used to render a reorderable item in CardView.
 */
export function BaseCardViewReorderableItem<K extends string | number, D>({
  currentKey,
  children,
  dragKey,
  setDragKey,
  onReorder,
  rootRef,
  gutterSize,
  columns,
  context
}: BaseCardViewReorderableItemProps<K> & { context: ListItemRendererContext<K, D> }) {
  const itemRef = useRef<HTMLDivElement | null>(null);
  const isOneColumn = columns === 1;
  const isFirstColumn = context.index % columns === 0;
  const isLastColumn = context.index % columns === columns - 1;
  const variantClasses = multiVariantStyles({
    itemOverlayFront: gutterSize
  });
  const variantClassesVertical = multiVariantStyles({
    itemOverlayFrontVertical: gutterSize
  });
  const itemStyles = {
    itemOverlayFront: isOneColumn ? variantClassesVertical : variantClasses,
    itemOverlayRear: isOneColumn ? styles.itemOverlayRearVertical : styles.itemOverlayRear,
    itemDragger: styles.itemDragger
  };
  const getItem = useCallback(() => itemRef?.current?.closest(ITEM_SELECTOR) as HTMLElement, []);
  const setTransferData = useCallback(
    (event: DragEvent) => {
      event.dataTransfer?.setData('text/cardview-items-key', JSON.stringify(context.metadata.key));
    },
    [context.metadata.key]
  );
  const { rootProps, isDragged, overlayZoneBeforeProps, overlayZoneAfterProps } =
    useReorderableItem({
      currentKey,
      isDisabled: onReorder ? false : true,
      draggable: onReorder ? true : false,
      dragKey,
      setDragKey,
      onReorder,
      rootRef,
      ref: itemRef,
      getItem,
      setTransferData,
      itemStyles,
      itemSelector: ITEM_SELECTOR,
      isVertical: isOneColumn
    });

  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  const overlayZoneBeforeClass = classNames([
    overlayZoneBeforeProps?.class,
    !isOneColumn && !isRtl && isFirstColumn && styles.itemOverlayFrontEdge,
    !isOneColumn && isRtl && isLastColumn && styles.itemOverlayFrontEdge
  ]);

  const overlayZoneAfterClass = classNames([
    overlayZoneAfterProps?.class,
    !isOneColumn && !isRtl && isLastColumn && styles.itemOverlayRearEdge,
    !isOneColumn && isRtl && isFirstColumn && styles.itemOverlayRearEdge
  ]);

  return (
    <div {...rootProps} draggable={false}>
      {children}
      {!isDragged && dragKey && (
        <>
          <div {...overlayZoneBeforeProps} class={overlayZoneBeforeClass}></div>
          <div {...overlayZoneAfterProps} class={overlayZoneAfterClass}></div>
        </>
      )}
    </div>
  );
}
